/**
 * Index-strip sparkline geometry (F5, plan §1.6 · §3.3).
 *
 * A component receives coordinates and never computes them, so the two-segment
 * polyline that represents a window's total return lives here — pure, no DOM,
 * property-testable — rather than in the React tree that draws it.
 *
 * The mid point is deliberately at HALF the end scale, which bends the line
 * outward from the baseline instead of drawing a straight ramp: the demo's
 * fifth RNG stream (an actual random walk) is NOT ported, and a straight line
 * would read as data it does not have. Both offsets clamp to the same envelope
 * so an extreme return saturates at the viewBox edge rather than escaping it.
 */

/** viewBox width. x = 0 is the window start, x = width the window end. */
export const SPARK_VIEWBOX_WIDTH = 100;
/** viewBox height. Sized so the baseline ± the clamp fits with 1px of margin. */
export const SPARK_VIEWBOX_HEIGHT = 26;
/** y of a zero / unknown return. SVG y grows DOWNWARD: a gain is a SMALLER y. */
export const SPARK_BASELINE_Y = 13;
/** Maximum excursion either side of the baseline, in viewBox units. */
export const SPARK_CLAMP = 12;
/** Return → offset multiplier at the end point (x = width). */
export const SPARK_END_SCALE = 40;
/** Return → offset multiplier at the mid point (x = width / 2). */
export const SPARK_MID_SCALE = 20;
/** Decimal places in emitted coordinates — keeps the `d` attribute stable. */
export const SPARK_PRECISION = 1;

/** `viewBox` attribute for the SVG that draws a `sparkPath` result. */
export const SPARK_VIEWBOX = `0 0 ${String(SPARK_VIEWBOX_WIDTH)} ${String(SPARK_VIEWBOX_HEIGHT)}`;

function offsetY(ret: number, scale: number): number {
  return SPARK_BASELINE_Y - Math.max(-SPARK_CLAMP, Math.min(SPARK_CLAMP, ret * scale));
}

/** Simple two-segment polyline representing total return — no unported RNG stream. */
export function sparkPath(ret: number | null): string {
  if (ret === null || !Number.isFinite(ret)) {
    return `M0 ${String(SPARK_BASELINE_Y)} L${String(SPARK_VIEWBOX_WIDTH)} ${String(SPARK_BASELINE_Y)}`;
  }
  const endY = offsetY(ret, SPARK_END_SCALE);
  const midY = offsetY(ret, SPARK_MID_SCALE);
  const midX = SPARK_VIEWBOX_WIDTH / 2;
  return `M0 ${String(SPARK_BASELINE_Y)} L${String(midX)} ${midY.toFixed(SPARK_PRECISION)} L${String(SPARK_VIEWBOX_WIDTH)} ${endY.toFixed(SPARK_PRECISION)}`;
}
